import { useEffect, useState, } from "react";
import styled from "styled-components";
import { IconButton, } from "../IconButton";
import { Chip, } from "../Chip";
import { getTabularRasaStore, } from "./RasaStore";
import { Div, Stream, } from "@bracket/ui/nodes";
import { getTheme, } from "@bracket/ui/themes";
/**
 * Pagination controls for TabularRasa.
 *
 * Renders a compact, numbered pagination bar with ellipses when there are gaps
 * between visible page numbers. The current page is highlighted and the list is
 * accessible: buttons include aria-current on the active page and preserve
 * native button semantics. Visual styles are intentionally minimal and can be
 * composed within layout containers.
 */

/**
 * Props for TabularRasaPagination
 * - current: 1-based current page index
 * - total: total number of pages (>= 0)
 * - onSelect: callback when a page button is clicked
 * - siblingPageCount: number of pages to show on each side of the current page (default: 1)
 * - align: alignment of the pagination bar (default: "RIGHT")
 * - first: first displayed row number (1-based)
 * - last: last displayed row number (1-based)
 * - totalItems: total number of items across all pages
 * 
 * Pagination always shows first and last page with current page ± siblingPageCount
 */
export declare type TabularRasaPaginationProps = {
	id: string;
	onSelect?: (page: number) => void;
	showResultsText?: boolean;

	current?: number;
	total?: number;

	siblingPageCount?: number;
	align?: "LEFT" | "CENTER" | "RIGHT";
	first?: number;
	last?: number;
	totalItems?: number;
};

/**
 * TabularRasaPagination
 *
 * Example:
 * ```tsx
 * <PaginationBar>
 *   <PageButton type="button" onClick={...}>Previous</PageButton>
 *   <TabularRasaPagination current={2} total={10} onSelect={setPage} />
 *   <PageButton type="button" onClick={...}>Next</PageButton>
 * </PaginationBar>
 * ```
 */
export const TabularRasaPagination = ({
	id,
	onSelect = () => {},
	siblingPageCount = 1,
	showResultsText = true,
	align = "RIGHT",
	first,
	last,
}: TabularRasaPaginationProps) => {

	if(!id) {
		throw new Error("TabularRasaPagination id is required");
	}
	const { deviceType, totalPages, firstPage, lastPage, totalItems, itemsPerPage, currentPage, nextPage, previousPage, toPage, } = getTabularRasaStore(id)();

	const items = deviceType === "MOBILE" ? buildMobilePageRange(currentPage, totalPages) : buildPageRange(currentPage, totalPages, siblingPageCount);
	const isFirst = currentPage === 1;
	const isLast = currentPage === totalPages || totalPages === 0;
	const isEmpty = totalItems <= 1;
	const canShowResultsText = firstPage !== undefined && lastPage !== undefined && totalItems !== undefined;
	const firstItem = () => (currentPage - 1) * itemsPerPage + 1;
	const lastItem = () => Math.min(currentPage * itemsPerPage, totalItems);
	const resultsText = canShowResultsText &&showResultsText ? `Showing ${firstItem()} to ${lastItem()} of ${totalItems} results` : null;

	return (
		<PaginationContainer>
			{showResultsText && resultsText && (
				<ResultsText>{resultsText}</ResultsText>
			)}
			<PaginationBar $empty={isEmpty} $align={align}>
				{!isEmpty && (
					<>
						<IconButton
							icon="ArrowLeft"
							aria-label="Previous page"
							label="Previous"
							disabled={isFirst}
							onClick={() => previousPage() && onSelect(Math.max(1, currentPage - 1))}
							iconSize="1.5rem"
						/>
						{deviceType !== "MOBILE" && items.map((it, idx) => {
							if(it === "...") {
								const prev = String(items[idx - 1] ?? "start");
								const next = String(items[idx + 1] ?? "end");
								return <Ellipsis key={`e-${prev}-${next}`}>…</Ellipsis>;
							}
							const page = it as number;
							const isActive = page === currentPage;
							return (
								<Chip
									key={`p-${page}`}
									label={page}
									onClick={() => toPage(page) && onSelect(page)}
									inactive={!isActive}
									aria-current={isActive ? "page" : undefined}
									circular
									color={isActive ? "primary" : "surface_on"}
								/>
							);
						})}
						<IconButton
							icon="ArrowRight"
							aria-label="Next page"
							label="Next"
							iconAlign="end"	
							disabled={isLast}
							onClick={() => nextPage() && onSelect(Math.min(totalPages, currentPage + 1))}
							iconSize="1.5rem"
							nowrap
						/>
					</>
				)}
			</PaginationBar>
		</PaginationContainer>
	);
};

/**
 * Container for pagination bar and results text
 */
const PaginationContainer = styled(Stream)`
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	margin-top: 1rem;

	@media (max-width: 768px) {
		flex-direction: column-reverse;
		align-items: center;
		gap: 0.5rem;
	}
`;

/**
 * Flex container intended to hold Prev/Next and the numbered `TabularRasaPagination`.
 */
export const PaginationBar = styled.div<{ $empty?: boolean; $align?: "LEFT" | "CENTER" | "RIGHT"; }>`
	display: flex;
	align-items: center;
	justify-content: ${({ $align }) => {
		if($align === "LEFT") return "flex-start";
		if($align === "CENTER") return "center";
		return "flex-end";
	}};
	gap: 0.5rem;
	visibility: ${({ $empty }) => ($empty ? "hidden" : "visible")};
	height: ${({ $empty }) => ($empty ? "auto" : "auto")};
	user-select: none;
	transform-origin: right;
	scale: 0.85;
	@media (max-width: 768px) {
		justify-content: space-between;
		width: 100%;
		transform-origin: center;
	}
`;
	
/**
 * Results text showing "Showing X to Y of Z results"
 */
const ResultsText = styled(Stream)`
	color: ${() => getTheme().surface_on};
	text-align: left;
	text-align: center;
`;

/**
 * Visual ellipsis separator between distant page groups.
 */
const Ellipsis = styled.span`
	padding: 0 0.25rem;
	color: ${() => getTheme().surface_variant_on};
`;


/** Build simplified mobile page range: returns empty array (no page numbers shown on mobile). */
const buildMobilePageRange = (_current: number, _total: number): (number | "...")[] => {
	return [];
}


const buildPageRange = (_current: number, total: number, siblingPageCount = 1, ): (number | "...")[] => {
	if(total <= 1) return [];
	const range: (number | "...")[] = [];

	const first = 1;
	const last = total;

	range.push(first);

	let pagesToShow: number[] = [];
	if(_current <= 3) {
		// For pages 1-3, show pages 2, 3, 4
		pagesToShow = [2, 3, 4].filter(p => p < last);
	} else if(_current >= last - 2) {
		// For pages (last-2) to last, show last-3, last-2, last-1
		pagesToShow = [last - 3, last - 2, last - 1].filter(p => p > first && p < last);
	} else {
		// For middle pages, show current-1, current, current+1
		const leftStart = _current - siblingPageCount;
		const rightEnd = _current + siblingPageCount;
		for(let p = leftStart; p <= rightEnd; p++) {
			if(p > first && p < last) {
				pagesToShow.push(p);
			}
		}
	}

	// Add ellipsis if there's a gap after first
	if(pagesToShow.length > 0 && pagesToShow[0] > first + 1) {
		range.push("...");
	}

	for(const p of pagesToShow) {
		range.push(p);
	}

	// Add ellipsis if there's a gap before last
	if(pagesToShow.length > 0 && pagesToShow[pagesToShow.length - 1] < last - 1) {
		range.push("...");
	}

	// Always add last page if total > 1
	if(last > first) range.push(last);

	return range;
}


